/**
 * VerifiedBadge — a small "verified sender" mark shown next to a sender when the
 * message passed email authentication. The signal is derived from the
 * provider-reported SPF/DKIM/DMARC results the receiving server asserted (parsed
 * server-side from the headers), so it can't be spoofed from message content.
 *
 * "Verified" = DMARC passed (the strongest signal — it requires an aligned
 * SPF or DKIM pass), OR both SPF and DKIM passed. Anything else → no badge (we
 * show a positive mark only; absence is not a "danger" claim).
 */

import { ShieldCheck } from 'lucide-react';
import type { EmailAuthResultsDto } from '@/lib/api';

/** Whether the sender is authentication-verified (see the module doc for the rule). */
export function isSenderVerified(auth: EmailAuthResultsDto | null | undefined): boolean {
  if (!auth) return false;
  return auth.dmarc === 'pass' || (auth.spf === 'pass' && auth.dkim === 'pass');
}

export function VerifiedBadge({
  auth,
  className,
}: {
  auth: EmailAuthResultsDto | null | undefined;
  className?: string;
}) {
  if (!isSenderVerified(auth)) return null;
  return (
    <span
      title="Verified sender — passed SPF/DKIM/DMARC authentication"
      aria-label="Verified sender"
      className={`inline-flex shrink-0 text-emerald-600 dark:text-emerald-400 ${className ?? ''}`}
    >
      <ShieldCheck className="h-3.5 w-3.5" />
    </span>
  );
}
